import type { LocationQuery, RouteLocationRaw } from 'vue-router'
import { checkUserType, getFrontendUrl, getPortalUrl, type UserType } from '../composables/useUserTypeCheck'

const redirectStorageKey = 'postLoginRedirect'
const validUserTypes = new Set<UserType>(['ibedrijf', 'inspecteur', 'opdrachtgever', 'installateur', 'unknown'])
const defaultPortalRoute = '/'
const authLinkRecoveryPath = '/auth/link-recovery'

export interface AuthLinkRecoveryState {
  email: string
  reason: 'expired-link' | 'invalid-link'
  redirect: string
}

interface WrongAppState {
  userTypes: UserType[]
  redirectUrl: string
}

const normalizeRedirect = (value: string) => {
  const trimmedValue = value.trim()

  if (!trimmedValue.startsWith('/') || trimmedValue.startsWith('//')) {
    return defaultPortalRoute
  }

  return trimmedValue
}

export const sanitizeRedirectTarget = (value: string | null | undefined) => {
  if (!value) return defaultPortalRoute

  try {
    const decodedValue = decodeURIComponent(value)
    return normalizeRedirect(decodedValue)
  } catch {
    return normalizeRedirect(value)
  }
}

/**
 * Validate an absolute cross-app redirect URL (e.g. the "wrong app" target that
 * ultimately reaches `window.location.href`). Unlike `sanitizeRedirectTarget`
 * (which is for in-app paths), this accepts a full URL but only if its origin is
 * one of our known application origins — otherwise it is an open-redirect vector.
 * Falls back to the frontend app URL when validation fails.
 */
export const sanitizeAppRedirectUrl = (value: string | null | undefined): string => {
  const fallback = getFrontendUrl()
  if (!value) return fallback

  const toOrigin = (url: string): string | null => {
    try {
      return new URL(url).origin
    } catch {
      return null
    }
  }

  const allowedOrigins = new Set(
    [
      getFrontendUrl(),
      getPortalUrl(),
      typeof window !== 'undefined' ? window.location.origin : null,
    ]
      .map((url) => (url ? toOrigin(url) : null))
      .filter((origin): origin is string => Boolean(origin)),
  )

  try {
    const base = typeof window !== 'undefined' ? window.location.origin : undefined
    const target = new URL(value, base)
    return allowedOrigins.has(target.origin) ? target.toString() : fallback
  } catch {
    return fallback
  }
}

export const sanitizeEmailPrefill = (value: string | null | undefined) => {
  if (!value) return ''

  const normalizedValue = value.trim()
  if (!normalizedValue) return ''

  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedValue) ? normalizedValue : ''
}

export const buildPortalAuthCallbackUrl = ({
  redirect,
  email,
}: {
  redirect: string
  email?: string | null
}) => {
  const callbackUrl = new URL('/auth/callback', window.location.origin)
  callbackUrl.searchParams.set('redirect', sanitizeRedirectTarget(redirect))

  const sanitizedEmail = sanitizeEmailPrefill(email)
  if (sanitizedEmail) {
    callbackUrl.searchParams.set('email', sanitizedEmail)
  }

  return callbackUrl.toString()
}

export const parseRecoveryState = (query: LocationQuery): AuthLinkRecoveryState => {
  const redirectParam = Array.isArray(query.redirect) ? query.redirect[0] : query.redirect
  const emailParam = Array.isArray(query.email) ? query.email[0] : query.email
  const reasonParam = Array.isArray(query.reason) ? query.reason[0] : query.reason

  return {
    redirect: sanitizeRedirectTarget(typeof redirectParam === 'string' ? redirectParam : defaultPortalRoute),
    email: sanitizeEmailPrefill(typeof emailParam === 'string' ? emailParam : ''),
    reason: reasonParam === 'invalid-link' ? 'invalid-link' : 'expired-link',
  }
}

export const buildAuthLinkRecoveryRoute = ({
  redirect,
  email,
  reason,
}: Partial<AuthLinkRecoveryState>): RouteLocationRaw => {
  const sanitizedRedirect = sanitizeRedirectTarget(redirect)
  const sanitizedEmail = sanitizeEmailPrefill(email)
  const resolvedReason = reason === 'invalid-link' ? 'invalid-link' : 'expired-link'

  return {
    path: authLinkRecoveryPath,
    query: {
      redirect: sanitizedRedirect,
      reason: resolvedReason,
      ...(sanitizedEmail ? { email: sanitizedEmail } : {}),
    },
  }
}

export const storePostLoginRedirect = (path: string) => {
  if (typeof window === 'undefined') return

  try {
    window.sessionStorage.setItem(redirectStorageKey, path)
  } catch (error) {
    console.warn('[AuthNavigation] Failed to store post-login redirect:', error)
  }
}

export const getStoredPostLoginRedirect = (): string | null => {
  if (typeof window === 'undefined') return null

  try {
    return window.sessionStorage.getItem(redirectStorageKey)
  } catch (error) {
    console.warn('[AuthNavigation] Failed to read post-login redirect:', error)
    return null
  }
}

export const clearStoredPostLoginRedirect = () => {
  if (typeof window === 'undefined') return

  try {
    window.sessionStorage.removeItem(redirectStorageKey)
  } catch (error) {
    console.warn('[AuthNavigation] Failed to clear post-login redirect:', error)
  }
}

export const buildWrongAppRoute = (userTypes: UserType[], redirectUrl?: string | null): RouteLocationRaw => ({
  path: '/wrong-app',
  query: {
    types: userTypes.join(','),
    redirectUrl: redirectUrl || getFrontendUrl(),
  },
})

export const parseWrongAppQuery = (query: LocationQuery): WrongAppState => {
  const typesParam = Array.isArray(query.types) ? query.types[0] : query.types
  const redirectUrlParam = Array.isArray(query.redirectUrl) ? query.redirectUrl[0] : query.redirectUrl

  const userTypes: UserType[] = typeof typesParam === 'string' && typesParam.length > 0
    ? typesParam.split(',').filter((type): type is UserType => validUserTypes.has(type as UserType))
    : ['unknown']

  return {
    userTypes,
    // SECURITY: the redirectUrl comes straight from the query string and reaches
    // window.location.href in WrongAppRedirect.vue, so it must be constrained to
    // our known app origins to avoid an open redirect.
    redirectUrl: sanitizeAppRedirectUrl(
      typeof redirectUrlParam === 'string' && redirectUrlParam.length > 0 ? redirectUrlParam : null,
    ),
  }
}

export async function resolvePortalRouteForUser(userId: string, redirectTarget: string): Promise<RouteLocationRaw> {
  const result = await checkUserType(userId)

  if (result.isAllowed) {
    return sanitizeRedirectTarget(redirectTarget)
  }

  return buildWrongAppRoute(result.types, result.redirectUrl)
}
